/**
 * The e-signature ceremony for the `awaiting_signature` step. Mounted by
 * FlowRunner when `SIGNATURE_SUBMITTED` is available.
 *
 * This screen is a legal act, not a form, and three of its rules follow from
 * that rather than from taste:
 *
 *  * **The document is shown, and its digest is shown verbatim.** A signature
 *    over bytes the signer could not inspect is not a signature under ETA §26 —
 *    which is why the backend serves `…/signature/document` at all. The digest
 *    is never truncated: an abbreviated hash cannot be compared against
 *    anything.
 *  * **A failed preview disables signing.** Fail-closed: if the applicant
 *    cannot see what they are signing, offering them a Sign button would ask
 *    them to attest to bytes they never saw.
 *  * **Consent is an explicit checkbox, not a bare button.** The gate here is at
 *    least as strong as `ConsentStep`'s, which guards a merely informational
 *    screen.
 *
 * Declining is the applicant's own act and fires `SIGNATURE_DECLINED`, which
 * the seed routes to manual review — not to a rejection. It is confirmed
 * inline (the same local two-step `ConsentStep` uses) rather than through
 * `window.confirm`, which cannot be styled, translated, or tested.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CollectionFlowError,
  getSignatureDescriptor,
  getSignatureDocument,
  postCollectionFlowEvent,
} from './client';
import { geolocationReason } from './attemptReason';
import { requestPosition, type PositionDenial } from './geolocation';
import { LocationBlocked } from './LocationBlocked';
import { reportAttempt } from './reportAttempt';
import { SignaturePad, type SignaturePadHandle } from './SignaturePad';
import type { CollectionFlowSnapshot, SignatureCapture, SignatureDescriptor } from './types';
import { StatusGlyph } from '../ui/StatusGlyph';

export const SIGNATURE_SUBMITTED_EVENT = 'SIGNATURE_SUBMITTED';
export const SIGNATURE_DECLINED_EVENT = 'SIGNATURE_DECLINED';

/**
 * Version of the consent wording recorded alongside the signature. BUMP THIS
 * whenever the `flow.sign.consent` copy changes: the stored version is what
 * later proves which text the applicant actually agreed to, so leaving it stale
 * after a copy change silently misattributes their consent.
 */
export const SIGNATURE_CONSENT_VERSION = 'sig-consent-v1';

type Phase = 'loading' | 'unavailable' | 'notReady' | 'review' | 'submitting' | 'declining';

interface SignatureStepProps {
  token: string;
  /** Receives the next snapshot after the flow advances. */
  onAdvance: (snapshot: CollectionFlowSnapshot) => void;
}

/**
 * Self-asserted device characteristics for the ceremony, recorded so the
 * device-intelligence report can compare the signing device against the capture
 * devices. Never authoritative — and never fatal: anything unreadable is simply
 * omitted rather than guessed.
 */
function describeDevice(): SignatureCapture['device'] {
  const device: NonNullable<SignatureCapture['device']> = {};
  try {
    if (navigator.platform) device.platform = navigator.platform;
    device.screen = `${window.screen.width}x${window.screen.height}@${window.devicePixelRatio}`;
    device.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    // A browser that hides any of these is not a reason to block a signature.
  }
  return Object.keys(device).length > 0 ? device : undefined;
}

export function SignatureStep({ token, onAdvance }: SignatureStepProps) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>('loading');
  const [descriptor, setDescriptor] = useState<SignatureDescriptor | null>(null);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [documentFailed, setDocumentFailed] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDecline, setConfirmingDecline] = useState(false);
  /** Set when the browser would not give up a position. Blocks signing. */
  const [denial, setDenial] = useState<PositionDenial | null>(null);
  // The drawn signature lives in the canvas, read once at submit. A ref rather
  // than state: mirroring every stroke into React would re-render the whole
  // ceremony per pointer move for a value only the submit handler reads.
  const padRef = useRef<SignaturePadHandle | null>(null);

  // Load the descriptor, then the bytes it names. The object URL is owned by
  // THIS mount and revoked on unmount — a revoked URL outliving its owner
  // renders as a broken document on the screen that matters most.
  useEffect(() => {
    let active = true;
    let objectUrl: string | null = null;

    void (async () => {
      try {
        const found = await getSignatureDescriptor(token);
        if (!active) return;
        if (!found) {
          setPhase('unavailable');
          return;
        }
        setDescriptor(found);
        setPhase('review');
        try {
          const blob = await getSignatureDocument(token);
          if (!active) return;
          objectUrl = URL.createObjectURL(blob);
          setDocumentUrl(objectUrl);
        } catch {
          if (active) setDocumentFailed(true);
        }
      } catch (e: unknown) {
        if (!active) return;
        setError(errorMessage(e));
        setPhase('unavailable');
      }
    })();

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [token]);

  const sign = useCallback(async () => {
    if (!descriptor) return;
    setPhase('submitting');
    setError(null);
    setDenial(null);

    // Where the applicant is, captured NOW rather than reused from consent.
    // Consent may have happened days ago on another device; this is the fix
    // that gets written into the document's `/Location` and onto its visible
    // stamp, so it has to describe the act it is attached to.
    //
    // Resolved before the request so a refusal never reaches the server as a
    // 400 the applicant would see as "something went wrong".
    const position = await requestPosition();
    if (!position.ok) {
      setDenial(position.reason);
      setPhase('review');
      // The only server-side trace — see the same note in ConsentStep.
      reportAttempt(token, {
        stepKey: 'awaiting_signature',
        stepKind: 'geolocation',
        reason: geolocationReason(position.reason),
        origin: 'client',
        attemptKey: `geolocation:signature:${position.reason}`,
      });
      return;
    }

    try {
      // No `documentFileId`: the subject is operator-designated server-side and
      // the payload rejects unknown keys, so naming a file here would 400 — and
      // is exactly the choice the applicant must not have.
      const payload: SignatureCapture = {
        consentTextVersion: SIGNATURE_CONSENT_VERSION,
        device: describeDevice(),
        geolocation: position.fix,
      };
      // The drawing, when there is one. Read from the canvas at THIS moment so
      // what is sent is exactly what is on screen; omitted entirely when empty
      // — the backend treats absence and unusable identically (sign without a
      // picture), so an applicant who skips the pad loses nothing.
      const visual = padRef.current?.toPngBase64();
      if (visual) payload.visualImageBase64 = visual;
      onAdvance(await postCollectionFlowEvent(token, SIGNATURE_SUBMITTED_EVENT, payload));
    } catch (e: unknown) {
      // 409 `signature_not_designated` is not a failure the applicant caused or
      // can retry past: the designation was cleared or refreshed between load
      // and submit. Rendering the generic "something went wrong" would be a lie
      // of omission — the same distinction `step.ts` draws between `blocked`
      // and `review`.
      if (e instanceof CollectionFlowError && e.status === 409) {
        setPhase('notReady');
        return;
      }
      setError(errorMessage(e));
      setPhase('review');
    }
  }, [descriptor, onAdvance, token]);

  const decline = useCallback(async () => {
    setPhase('declining');
    setError(null);
    try {
      onAdvance(await postCollectionFlowEvent(token, SIGNATURE_DECLINED_EVENT));
    } catch (e: unknown) {
      setError(errorMessage(e));
      setPhase('review');
      setConfirmingDecline(false);
    }
  }, [onAdvance, token]);

  if (phase === 'loading') {
    return (
      <div className="card enter" data-testid="signature-step">
        <div className="loading">
          <span className="spinner" aria-hidden="true" />
          <p>{t('flow.loading', { defaultValue: 'Loading…' })}</p>
        </div>
      </div>
    );
  }

  // No pending request for this case. Deliberately NOT a generic error and
  // deliberately not a Sign button over a guessed document: the SPA has nothing
  // to show and nothing valid to submit, and saying so is the honest state.
  if (phase === 'unavailable') {
    return (
      <div className="card enter" data-testid="signature-unavailable">
        <StatusGlyph kind="wait" />
        <h1 className="step-title">
          {t('flow.sign.unavailableTitle', { defaultValue: 'Nothing to sign yet' })}
        </h1>
        <p className="step-text">
          {t('flow.sign.unavailableBody', {
            defaultValue:
              'The document for this step is not ready. You can close this page — we will let you know when it is.',
          })}
        </p>
        {error ? (
          <p className="alert" role="alert">
            {t('flow.error', { defaultValue: 'Something went wrong.' })}
          </p>
        ) : null}
      </div>
    );
  }

  // The submit came back 409: an operator has to designate (or re-designate)
  // the subject before this ceremony can run. Distinct copy from `unavailable`
  // because the applicant just read a document and pressed Sign — telling them
  // "nothing to sign yet" without acknowledging that something changed would
  // read as the app losing their work.
  if (phase === 'notReady') {
    return (
      <div className="card enter" data-testid="signature-not-ready">
        <StatusGlyph kind="wait" />
        <h1 className="step-title">
          {t('flow.sign.notReadyTitle', { defaultValue: 'Not ready to sign yet' })}
        </h1>
        <p className="step-text">
          {t('flow.sign.notReadyBody', {
            defaultValue:
              'This document is not ready for signing right now. Nothing you did went wrong — our team needs to prepare it, and we will let you know when it is ready.',
          })}
        </p>
      </div>
    );
  }

  const busy = phase === 'submitting' || phase === 'declining';

  const canSign = agreed && !documentFailed && !busy;

  // The decline confirmation HIDES the review card rather than replacing it.
  // Unmounting would discard the signature pad's canvas — a canvas bitmap
  // lives exactly as long as its element — so "Decline → changed my mind →
  // Back" would silently erase a drawing the applicant just made. `hidden`
  // (display:none) keeps the element, and the drawing, alive.
  return (
    <>
      {confirmingDecline ? (
        <div className="card enter" data-testid="signature-decline-confirm">
          <h1 className="step-title">
            {t('flow.sign.declineTitle', { defaultValue: 'Decline to sign?' })}
          </h1>
          <p className="step-text">
            {t('flow.sign.declineBody', {
              defaultValue:
                'Your verification will be passed to our team to review instead. You can go back and sign if you change your mind.',
            })}
          </p>
          <div className="actions">
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() => setConfirmingDecline(false)}
              data-track="back"
            >
              {t('flow.sign.declineBack', { defaultValue: 'Back' })}
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              disabled={busy}
              data-event={SIGNATURE_DECLINED_EVENT}
              onClick={() => void decline()}
              data-track="confirm_decline_signature"
            >
              {t('flow.sign.declineConfirm', { defaultValue: 'Yes, decline' })}
            </button>
          </div>
        </div>
      ) : null}
      <div className="card enter" data-testid="signature-step" hidden={confirmingDecline}>
      <h1 className="step-title">
        {t('flow.sign.title', { defaultValue: 'Review and sign this document' })}
      </h1>
      <p className="step-text">
        {t('flow.sign.intro', {
          defaultValue: 'Read the document below. Your signature is legally binding.',
        })}
      </p>

      <DocumentPreview
        failed={documentFailed}
        mimeType={descriptor?.mimeType ?? null}
        title={descriptor?.fileName ?? t('flow.sign.untitled', { defaultValue: 'Document' })}
        url={documentUrl}
      />

      {/*
        The digest, verbatim and unabbreviated, labelled with its algorithm.
        This is what the applicant is actually attesting to — the label is what
        makes the hex interpretable, and truncating it would make it useless for
        the comparison it exists to enable.
      */}
      <dl className="sign-digest">
        <dt className="details-label">
          {t('flow.sign.digestLabel', {
            algorithm: descriptor?.hashAlgorithm ?? '',
            defaultValue: 'Document fingerprint ({{algorithm}})',
          })}
        </dt>
        <dd className="sign-digest-value" data-testid="signature-digest">
          {descriptor?.digestHex}
        </dd>
      </dl>

      <SignaturePad ref={padRef} disabled={busy} />

      <label className="sign-consent">
        <input
          type="checkbox"
          checked={agreed}
          disabled={busy}
          data-testid="signature-consent"
          onChange={(e) => setAgreed(e.target.checked)}
        />
        <span>
          {t('flow.sign.consent', {
            defaultValue:
              'I have read this document and I agree to sign it electronically. I understand my electronic signature is as binding as a handwritten one.',
          })}
        </span>
      </label>

      {error ? (
        <p className="alert" role="alert">
          {t('flow.error', { defaultValue: 'Something went wrong.' })}
        </p>
      ) : null}

      {/*
        Shown INSTEAD of the generic error, not beside it: a blocked position is
        not "something went wrong", it is a specific thing the applicant may be
        able to change. `sign` returns to the `review` phase on denial, so the
        pad, the checkbox and the button are all still live for the retry.
      */}
      {denial && <LocationBlocked reason={denial} />}

      <div className="actions">
        <button
          type="button"
          className="btn"
          disabled={!canSign}
          data-event={SIGNATURE_SUBMITTED_EVENT}
          onClick={() => void sign()}
          data-track="sign_document"
        >
          {phase === 'submitting'
            ? t('flow.sign.signing', { defaultValue: 'Signing…' })
            : t('flow.sign.sign', { defaultValue: 'Sign document' })}
        </button>
        <button
          type="button"
          className="btn btn--ghost"
          disabled={busy}
          onClick={() => setConfirmingDecline(true)}
          data-track="decline_signature"
        >
          {t('flow.sign.decline', { defaultValue: 'Decline' })}
        </button>
      </div>
      </div>
    </>
  );
}

/**
 * The document itself. A failure here is surfaced rather than swallowed,
 * because the Sign button is disabled while it holds — the applicant is told
 * why they cannot proceed instead of finding a dead control.
 */
function DocumentPreview({
  failed,
  mimeType,
  title,
  url,
}: {
  failed: boolean;
  mimeType: string | null;
  title: string;
  url: string | null;
}) {
  const { t } = useTranslation();

  if (failed) {
    return (
      <p className="alert" role="alert" data-testid="signature-doc-error">
        {t('flow.sign.documentError', {
          defaultValue:
            'We could not open the document. You cannot sign something you cannot read, so please try again in a moment.',
        })}
      </p>
    );
  }
  if (!url) {
    return (
      <div className="loading">
        <span className="spinner" aria-hidden="true" />
        <p>{t('flow.sign.documentLoading', { defaultValue: 'Opening document…' })}</p>
      </div>
    );
  }

  return (
    <figure className="sign-document" data-testid="signature-document">
      {mimeType?.startsWith('image/') ? (
        <img src={url} alt={title} className="sign-document-frame" />
      ) : (
        <iframe src={url} title={title} className="sign-document-frame" />
      )}
      <figcaption className="sign-document-name">{title}</figcaption>
    </figure>
  );
}

function errorMessage(e: unknown): string {
  if (e instanceof CollectionFlowError) return `${e.status}`;
  return e instanceof Error ? e.message : 'error';
}
